import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, Check, ChevronDown } from "lucide-react";
import { merchantApi } from "@/lib/api/merchant";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/button";

/**
 * Country switcher for owners who hold more than one merchant ID.
 *
 * A merchant ID is bound to a country — that is what routing keys off — so a
 * business selling into India and Türkiye holds one per market, each with its
 * own API keys and its own balance. They share a login, and this moves the
 * session between them.
 *
 * Renders NOTHING for the single-market case, which is almost every merchant;
 * there is no point in a chooser with one entry.
 */

const FLAGS: Record<string, string> = {
  IN: "🇮🇳", TR: "🇹🇷", BR: "🇧🇷", CO: "🇨🇴", MX: "🇲🇽", PE: "🇵🇪",
};
const COUNTRY_NAMES: Record<string, string> = {
  IN: "India", TR: "Türkiye", BR: "Brazil", CO: "Colombia", MX: "Mexico", PE: "Peru",
};

interface Account {
  merchant_id: string;
  name: string;
  country: string;
  status: string;
  /** The login for this market — each merchant ID has its own. */
  username?: string | null;
  is_current: boolean;
}

export function AccountSwitcher() {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["m-accounts"],
    queryFn: merchantApi.accounts,
    // The set of markets changes when an operator provisions one, not while
    // someone is clicking around.
    staleTime: 5 * 60 * 1000,
  });

  const accounts: Account[] = (data as any)?.accounts ?? [];
  if (accounts.length < 2) return null;

  const current = accounts.find((a) => a.is_current) ?? accounts[0];

  async function choose(account: Account) {
    if (account.is_current) {
      setOpen(false);
      return;
    }
    setSwitching(account.merchant_id);
    setError(null);
    try {
      const res: any = await merchantApi.switchAccount(account.merchant_id);
      // A new session for a DIFFERENT merchant: every cached figure on screen
      // belongs to the old one, so the cache is dropped rather than refetched
      // piecemeal.
      setAuth(res.access_token, {
        id: res.merchant?.id,
        name: res.merchant?.name,
      });
      queryClient.clear();
      setOpen(false);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ?? "Could not switch account. Please try again."
      );
    } finally {
      setSwitching(null);
    }
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch country account"
      >
        <Globe size={15} />
        <span className="hidden sm:inline">
          {FLAGS[current.country] ?? ""} {current.country}
        </span>
        <ChevronDown size={13} className="opacity-60" />
      </Button>

      {open && (
        <>
          {/* Click-away. */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            role="listbox"
            className="absolute right-0 z-50 mt-1.5 w-64 overflow-hidden rounded-lg border bg-card shadow-lg"
          >
            <div className="border-b px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Your markets
            </div>
            {accounts.map((a) => {
              const inactive = a.status === "SUSPENDED" || a.status === "INACTIVE";
              return (
                <button
                  key={a.merchant_id}
                  role="option"
                  aria-selected={a.is_current}
                  disabled={inactive || switching !== null}
                  onClick={() => choose(a)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-[13px] hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="min-w-0">
                    <span className="mr-1.5">{FLAGS[a.country] ?? "🌐"}</span>
                    <span className="font-medium">
                      {COUNTRY_NAMES[a.country] ?? a.country}
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {inactive ? a.status.toLowerCase() : (a.username ?? a.name)}
                    </span>
                  </span>
                  {a.is_current ? (
                    <Check size={15} className="shrink-0 text-primary" />
                  ) : switching === a.merchant_id ? (
                    <span className="shrink-0 text-[11px] text-muted-foreground">…</span>
                  ) : null}
                </button>
              );
            })}
            <div className="border-t px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
              Each market has its own login, API keys and balance.
            </div>
            {error && (
              <div className="border-t bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
                {error}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
